/**
 * The parts of a bar that are not its notes: what `<attributes>` declares, what a
 * `<direction>` writes above the staff, and what a `<barline>` closes it with.
 *
 * These are kept apart from the cursor walk in `import-measure.ts` because they share
 * nothing with it: each reads one element and answers with values, while the walk is
 * entirely about where in the bar the next note lands.
 */

import type { EventId } from "../document/ids";
import { STEPS } from "../document/pitch";
import type { Step, Transposition } from "../document/pitch";
import type { StringTuning } from "../document/tab";
import type { FiguredBass } from "../document/figures";
import type { Harmony } from "../document/harmony";
import type {
  Clef,
  Direction,
  InstrumentChange,
  PartMeasure,
  SpannerKind,
  WordsStyle,
} from "../document/types";
import { attr, attrNumber, child, childrenNamed, numberOf, textOf } from "../xml/read";
import type { XmlElement } from "../xml/parse";
import type { DrumListing, DrumSighting } from "./import-drums";
import type { RosterInstrument } from "./import-header";
import { readJumpDirection } from "./import-jumps";
import type { JumpRead } from "./import-jumps";
import { clefOf, dynamicOf, tempoOf } from "./import-values";
import type { TempoRead, VoiceBuild } from "./import-values";

/** A line end read out of a bar, still naming only one of its two events. */
export interface OpenSpan {
  readonly kind: SpannerKind;
  readonly number: number;
  readonly from: EventId;
  readonly placement: "above" | "below";
}

/** A span the bar closed, with both of its ends. */
export interface ClosedSpan extends OpenSpan {
  readonly to: EventId;
}

/** What the reader carries from one bar of a part to the next. */
export interface PartState {
  divisions: number;
  staffCount: number;
  transposition: Transposition;
  /** Lines still open, by the pool and number MusicXML pairs their ends with. */
  readonly open: Map<string, OpenSpan>;
  readonly closed: ClosedSpan[];
  /**
   * The event that starts last in the last bar of the part that had one. By onset, not by
   * the order read: after a `<backup>` the note read last is another voice's, which may begin
   * before a line open over the bar does.
   */
  last: EventId | null;
  /** Everything the part is played on, by the roster id a bar names it with. */
  readonly roster: ReadonlyMap<string, RosterInstrument>;
  /** The part's own entry in that roster, which is what it is played on until it says. */
  readonly opening: RosterInstrument;
  /**
   * What the player has in their hands, and what they had in them at the first bar.
   *
   * Both stay `null` until one bar has been read, because what a part opens on is only
   * settled once its first `<attributes>` has said whether it transposes.
   */
  playing: InstrumentChange | null;
  opened: InstrumentChange | null;
  /** The drums this part lists, empty unless it is a percussion part. */
  readonly drums: DrumListing;
  /** Where each drum turned out to be written, filled in as the notes are read. */
  readonly seen: Map<string, DrumSighting>;
  /** Staves drawn with other than five lines, by staff index. */
  readonly staffLines: Map<number, number>;
  /**
   * Staves the file declared to be ossias, by the file's own 0-based staff index.
   *
   * An ossia is a staff of the part in MusicXML and a voice of the bar here, so the bars
   * are read with the file's numbering and renumbered afterwards. See `import-ossia.ts`.
   */
  readonly ossiaStaves: Set<number>;
  /** The strings the part turned out to be tuned to, or `null` while it has said none. */
  tuning: readonly StringTuning[] | null;
  /**
   * The part gathers its silent bars: some bar of it stated a multiple rest.
   *
   * A file says this by drawing it and not by declaring it, so it is learnt on the way
   * through the bars and read off afterwards into the document's page. The bars of the
   * run are in the file either way, so nothing about the music depends on it.
   */
  gathered: boolean;
}

/** Everything one bar of one part says, about itself and about the piece. */
export interface MeasureRead {
  readonly measure: PartMeasure;
  /** Clefs stated in this bar, by staff index: the opening clefs in bar one, changes later. */
  readonly clefs: Readonly<Record<number, Clef>>;
  readonly key: { fifths: number; mode: "major" | "minor" | null } | null;
  readonly time: { beats: number; beatType: number; symbol: "number" | "common" | "cut" } | null;
  readonly tempo: TempoRead | null;
  /**
   * What the part reads at in this bar: its own transposition, or a doubling's.
   *
   * Kept per bar rather than per part because the signature a part prints is read against
   * it, and a player who has picked a piccolo up is printing a different one from here on.
   */
  readonly transposition: Transposition;
  readonly rehearsal: string | null;
  readonly barline: string | null;
  readonly repeatStart: boolean;
  readonly repeatEnd: boolean;
  /** The `times` a backward repeat states, or `null` where it states none worth the name. */
  readonly repeatTimes: number | null;
  readonly endingStart: readonly number[] | null;
  readonly endingStop: boolean;
  /** The segno, the coda and the words that jump between them, as this bar states them. */
  readonly jumps: JumpRead;
  readonly breakBefore: "system" | "page" | null;
  /** `implicit="yes"`: the bar is not counted, which is how a pickup is written. */
  readonly implicit: boolean;
  /** Ticks the bar actually holds, which is what a short bar's length is read from. */
  readonly filled: number;
}

/** A line that has opened and is waiting for the note it starts on. */
export interface PendingSpan {
  readonly kind: SpannerKind;
  /** The pool its number belongs to, which is what the open line is keyed by. */
  readonly pool: string;
  readonly number: number;
  readonly placement: OpenSpan["placement"];
}

/** What a bar collects while it is being read, before it becomes a {@link MeasureRead}. */
export interface Walk {
  cursor: number;
  /** The furthest the cursor has reached, which a `<backup>` does not take back. */
  filled: number;
  readonly voices: Map<number, VoiceBuild>;
  readonly directions: Direction[];
  readonly pendingDirections: Omit<Direction, "id" | "anchor">[];
  readonly pendingSpans: PendingSpan[];
  /** The roster id of an instrument this bar hands the player, if one does. */
  instrumentId: string | null;
  /** The chord symbols read so far, each already at its tick within the bar. */
  readonly harmonies: Harmony[];
  /** The figured bass read so far, each stack already at its tick within the bar. */
  readonly figures: FiguredBass[];
  /**
   * Ticks a run of stacks has walked ahead of the cursor before the note they precede.
   *
   * A figure has no offset, so a second stack under one note is placed by the duration of
   * the first. This is reset when a note is written, because the next run starts there.
   */
  figureAhead: number;
  lastEvent: EventId | null;
  /** Where in the bar the part's {@link PartState.last} starts, or -1 before it has one here. */
  latest: number;
}

/** `<attributes>`: what changes about the part, and what it says about the piece. */
export function readAttributes(
  element: XmlElement,
  state: PartState,
  clefs: Record<number, Clef>,
): Pick<MeasureRead, "key" | "time"> {
  state.divisions = numberOf(element, "divisions", state.divisions);
  state.staffCount = Math.max(state.staffCount, numberOf(element, "staves", 1));

  for (const style of childrenNamed(element, "measure-style")) {
    if (child(style, "multiple-rest") !== null) state.gathered = true;
  }

  for (const node of childrenNamed(element, "clef")) {
    clefs[Math.max(0, Number(attr(node, "number") ?? 1) - 1)] = clefOf(node);
  }

  for (const node of childrenNamed(element, "staff-details")) {
    const staff = Math.max(0, Number(attr(node, "number") ?? 1) - 1);
    const lines = numberOf(node, "staff-lines", 5);
    if (lines > 0) state.staffLines.set(staff, lines);
    if (textOf(node, "staff-type") === "ossia") state.ossiaStaves.add(staff);
    // A tuning belongs to the instrument rather than to the staff, and an ossia is read on
    // the same instrument as the bar under it, so its restated strings say nothing new.
    const tuning = state.ossiaStaves.has(staff) ? null : tuningOf(node);
    if (tuning !== null) state.tuning = tuning;
  }

  const transpose = child(element, "transpose");
  if (transpose !== null) {
    // Field order follows the interface, so a document written out as JSON is byte-stable
    // whether it was built here or by hand.
    state.transposition = {
      chromatic: numberOf(transpose, "chromatic", 0),
      diatonic: numberOf(transpose, "diatonic", 0),
      octaveChange: numberOf(transpose, "octave-change", 0),
    };
  }

  const keyNode = child(element, "key");
  const timeNode = child(element, "time");
  const mode = textOf(keyNode, "mode");
  const symbol = attr(timeNode, "symbol");

  return {
    key:
      keyNode === null
        ? null
        : {
            fifths: numberOf(keyNode, "fifths", 0),
            mode: mode === "major" || mode === "minor" ? mode : null,
          },
    time:
      timeNode === null
        ? null
        : {
            beats: numberOf(timeNode, "beats", 4),
            beatType: numberOf(timeNode, "beat-type", 4),
            symbol: symbol === "common" || symbol === "cut" ? symbol : "number",
          },
  };
}

/** Accumulators for the things a bar says about the piece rather than about the part. */
export interface GlobalRead {
  tempo: MeasureRead["tempo"];
  rehearsal: string | null;
  barline: string | null;
  repeatStart: boolean;
  repeatEnd: boolean;
  repeatTimes: number | null;
  endingStart: readonly number[] | null;
  endingStop: boolean;
  readonly jumps: JumpRead;
  breakBefore: "system" | "page" | null;
}

/** Close an open line on the event it ends at. */
export function closeSpan(state: PartState, key: string, at: EventId | null): void {
  const open = state.open.get(key);
  if (open === undefined || at === null) return;
  state.closed.push({ ...open, to: at });
  state.open.delete(key);
}

function samePlaying(a: InstrumentChange, b: InstrumentChange): boolean {
  return (
    a.name === b.name &&
    a.sound === b.sound &&
    a.midiProgram === b.midiProgram &&
    a.transposition.chromatic === b.transposition.chromatic &&
    a.transposition.diatonic === b.transposition.diatonic &&
    a.transposition.octaveChange === b.transposition.octaveChange
  );
}

/**
 * The instrument a bar hands the player, or `null` where they play on.
 *
 * Two things in a file say it and either alone is enough. A `<sound><instrument-change>`
 * names a roster entry, which is how MusicXML says it and what this package writes. A
 * `<transpose>` that moves partway through a part says it too, and is all that older files
 * -- and several programs that still do not write the first -- ever say: a flute part that
 * suddenly reads a tone down is a flute part with a clarinet in its hands.
 *
 * The first bar settles what the part opened on instead of changing anything, since a
 * `<transpose>` there is the instrument's own. Everything after it is compared against
 * what the player is already holding, so a file that restates the same instrument every
 * bar -- some do -- is read as picking nothing up.
 */
export function instrumentTaken(state: PartState, named: string | null): InstrumentChange | null {
  const entry = (named === null ? null : state.roster.get(named)) ?? state.playing ?? state.opening;
  const taken: InstrumentChange = {
    name: entry.name,
    sound: entry.sound,
    midiProgram: entry.midiProgram,
    transposition: state.transposition,
  };

  if (state.playing === null) {
    state.playing = taken;
    state.opened = taken;
    return null;
  }
  if (samePlaying(taken, state.playing)) return null;
  state.playing = taken;
  return taken;
}

/** What one `<direction-type>` says about a held line, when it speaks about one at all. */
interface LineRead {
  /** Which set of numbers pairs this line's ends: the format numbers each kind separately. */
  readonly pool: string;
  readonly number: number;
  /** The line this end opens, or `null` when it closes one. */
  readonly kind: SpannerKind | null;
}

function octaveKind(direction: string | null, size: number): SpannerKind {
  if (size >= 15) return direction === "up" ? "15mb" : "15ma";
  return direction === "up" ? "8vb" : "8va";
}

/**
 * The held line one `<direction-type>` draws, if it draws one.
 *
 * `continue` and a pedal `change` mark a point inside a line rather than either of its ends.
 * The document holds only the two ends, so they are passed over rather than read as a second
 * start that would never be closed.
 */
function lineOf(type: XmlElement): LineRead | null {
  const wedge = child(type, "wedge");
  if (wedge !== null) {
    const spelling = attr(wedge, "type");
    if (spelling === "continue") return null;
    return {
      pool: "wedge",
      number: Number(attr(wedge, "number") ?? 1),
      kind: spelling === "stop" ? null : spelling === "diminuendo" ? "diminuendo" : "crescendo",
    };
  }

  const pedal = child(type, "pedal");
  if (pedal !== null) {
    const spelling = attr(pedal, "type");
    if (spelling !== "start" && spelling !== "stop" && spelling !== "sostenuto") return null;
    return {
      pool: "pedal",
      number: Number(attr(pedal, "number") ?? 1),
      kind: spelling === "stop" ? null : "pedal",
    };
  }

  const octave = child(type, "octave-shift");
  if (octave !== null) {
    const spelling = attr(octave, "type");
    if (spelling === "continue") return null;
    return {
      pool: "octave",
      number: Number(attr(octave, "number") ?? 1),
      kind: spelling === "stop" ? null : octaveKind(spelling, Number(attr(octave, "size") ?? 8)),
    };
  }

  return null;
}

/**
 * What a piece of text is, read out of how it is drawn.
 *
 * MusicXML says only what font the words are set in, so this is the mirror of the table in
 * `export-measure.ts`: heavy is about speed, leaning is something to feel, and upright is
 * something to do. Bold is tested first because a tempo mark is often bold *and* italic,
 * and it is the weight that makes it a tempo mark.
 */
function wordsStyleOf(words: XmlElement): WordsStyle {
  if (attr(words, "font-weight") === "bold") return "tempo";
  if (attr(words, "font-style") === "italic") return "expression";
  return "technique";
}

export function readDirection(
  element: XmlElement,
  walk: Walk,
  state: PartState,
  global: GlobalRead,
): void {
  const stated = attr(element, "placement");
  const placement = stated === "below" ? "below" : "above";
  const types = childrenNamed(element, "direction-type");

  // A direction handing the player another instrument is that change and nothing else: the
  // words in it name the instrument, and reading them as words as well would write the name
  // twice over -- once as the change, once as a word standing over the staff.
  const named = attr(child(child(element, "sound"), "instrument-change"), "id");
  if (named !== null) {
    walk.instrumentId = named;
    return;
  }

  const rehearsal = types.map((type) => textOf(type, "rehearsal")).find((text) => text !== null);
  if (rehearsal !== undefined && rehearsal !== null) global.rehearsal ??= rehearsal;

  // A jump is read before anything else and ends the direction: `To Coda` is an
  // instruction to the piece, not a word written over one player's staff, and a direction
  // that held a tempo or a hairpin says nothing about jumping, so nothing is lost by it.
  if (readJumpDirection(element, global.jumps)) return;

  for (const type of types) {
    const line = lineOf(type);
    if (line === null) continue;
    // A line opens before the note it starts on and closes after the note it ends on, so its
    // two ends bind in opposite directions: the start waits for the next note, the stop takes
    // the note already read. Making both wait would give a line drawn over a single long note
    // the same note at each end, and writing that back out would put the stop before the start.
    // A stop written before the bar's first note ends the line at the bar line, which is after
    // the note that starts last in the bar before. Read against this bar alone it had no
    // note to close on and the whole line was lost.
    if (line.kind === null) {
      closeSpan(state, `${line.pool}:${line.number}`, walk.lastEvent ?? state.last);
    } else walk.pendingSpans.push({ ...line, kind: line.kind, placement });
  }

  for (const type of types) {
    const dynamics = child(type, "dynamics");
    if (dynamics === null) continue;
    const mark = dynamicOf(dynamics);
    if (mark !== null) {
      walk.pendingDirections.push({ placement, content: { kind: "dynamic", mark } });
    }
  }

  const tempo = tempoOf(element);
  if (tempo !== null) {
    global.tempo ??= tempo;
    return;
  }
  if (rehearsal !== undefined && rehearsal !== null) return;
  for (const type of types) {
    const node = child(type, "words");
    const words = node?.text ?? null;
    if (node === null || words === null || words.trim() === "") continue;
    const style = wordsStyleOf(node);
    walk.pendingDirections.push({
      // A file that states no placement gets the one its style is drawn at, which is where
      // the program that wrote it will have put it: an expression belongs under the staff.
      placement: stated === null && style === "expression" ? "below" : placement,
      content: { kind: "words", text: words, style },
    });
  }
}

export function readBarline(element: XmlElement, global: GlobalRead): void {
  const location = attr(element, "location") ?? "right";
  const repeat = child(element, "repeat");
  const direction = attr(repeat, "direction");
  if (direction === "forward") global.repeatStart = true;
  if (direction === "backward") {
    global.repeatEnd = true;
    // How many times the section is played. A count below two is no count: a file that
    // writes `times="1"` on a wall it also draws is saying nothing a player could do.
    const times = attrNumber(repeat, "times", 0);
    if (Number.isInteger(times) && times >= 2) global.repeatTimes = times;
  }

  if (location !== "left") {
    const style = textOf(element, "bar-style");
    // A repeat bar is drawn `light-heavy` by definition; that is the repeat, not a style.
    global.barline = direction === "backward" && style === "light-heavy" ? null : style;
  }

  const ending = child(element, "ending");
  if (ending === null) return;
  const numbers = (attr(ending, "number") ?? "")
    .split(",")
    .map((raw) => Number(raw.trim()))
    .filter((value) => Number.isFinite(value) && value > 0);
  if (attr(ending, "type") === "start") global.endingStart = numbers;
  else global.endingStop = true;
}

/**
 * The strings a `<staff-details>` names, highest first, or `null` when it names none.
 *
 * The file counts its tunings by staff line from the bottom, and the document counts its
 * strings from the highest-sounding one, so the lines are sorted and then reversed. A file
 * that skips a line is read as the strings it did state rather than refused: a tab staff
 * with a line missing is still readable music.
 */
function tuningOf(node: XmlElement): readonly StringTuning[] | null {
  const strings = childrenNamed(node, "staff-tuning")
    .map((tuning) => ({
      line: Number(attr(tuning, "line") ?? 0),
      open: {
        step: (STEPS.includes(textOf(tuning, "tuning-step") as Step)
          ? textOf(tuning, "tuning-step")
          : "C") as Step,
        alter: numberOf(tuning, "tuning-alter", 0),
        octave: numberOf(tuning, "tuning-octave", 4),
      },
    }))
    .sort((a, b) => b.line - a.line)
    .map((entry) => entry.open);
  return strings.length === 0 ? null : strings;
}
